// src/components/ImageUploadSection.js
import React, { useCallback } from 'react';
import { Button, Typography, Grid, Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import styles from './ImageUploadSection.module.css';

const ImageUploadSection = ({ loading, isDragActive, onImageUpload, fileInputRef, selectedFile }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: useCallback((acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) onImageUpload({ target: { files: [file] } });
    }, [onImageUpload]),
    accept: 'image/*',
    noClick: true,
  });

  const previewUrl = selectedFile ? URL.createObjectURL(selectedFile) : null;

  return (
    <Grid container spacing={4} sx={{ 
      minHeight: '100vh', 
      p: 4,
      background: 'radial-gradient(circle at center, #1a1a1a 0%, #0a0a0a 100%)'
    }}>
      {/* Left Section - Content */}
      <Grid item xs={12} md={6}>
        <Box sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          pr: { md: 6 }
        }}>
          <Typography variant="h2" sx={{ 
            mb: 3,
            fontWeight: 800,
            background: 'linear-gradient(45deg, #2196f3, #90caf9)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.05em',
            fontSize: '3.5rem'
          }}>
            Remove Image Background
          </Typography>
          <Typography variant="h4" sx={{ 
            mb: 2, 
            fontWeight: 500,
            color: 'rgba(255,255,255,0.9)'
          }}>
            100% Automatically and Free
          </Typography>
          <Box sx={{
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
            position: 'relative',
            border: '1px solid rgba(255,255,255,0.1)',
            '& video': {
              width: '100%',
              height: 'auto',
              filter: 'brightness(0.95)'
            }
          }}>
            <video autoPlay loop muted playsInline>
              <source src="https://sb.kaleidousercontent.com/67418/x/681f13b37d/emilia_compressed.mp4" type="video/mp4" />
            </video>
          </Box>
          <Box sx={{ mt: 4 }}>
            <Typography variant="body1" sx={{ 
              opacity: 0.8, 
              mb: 2,
              fontSize: '1.1rem',
              letterSpacing: '0.5px',
              color: 'rgba(255,255,255,0.9)'
            }}>
              Features:
            </Typography>
            <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
              {['Instant Processing', 'No Watermarks', 'High Quality', 'Batch Support'].map((item) => (
                <li key={item} style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <Box sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '6px',
                    bgcolor: 'rgba(33,150,243,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                  }}>
                    <span style={{ color: '#2196f3', fontSize: '1rem' }}>✓</span>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '1rem',
                    fontWeight: 500 
                  }}>{item}</Typography>
                </li>
              ))}
            </ul>
          </Box>
        </Box>
      </Grid>

      {/* Right Section - Upload */}
      <Grid item xs={12} md={6}>
        <Box sx={{ 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center',
          pl: { md: 6 }
        }}>
          <div {...getRootProps()} className={`${styles.uploadSection} ${isDragActive ? styles.dragActive : ''}`}>
            <input {...getInputProps()} />
            <input
              type="file"
              accept="image/*"
              onChange={onImageUpload}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />

            <div className={styles.uploadContent}>
              {previewUrl ? (
                <div className={styles.imageCard}>
                  <div className={`${styles.imageContainer} ${loading ? styles.processing : ''}`}>
                    <img
                      src={previewUrl}
                      alt="Upload preview"
                      className={styles.previewImage}
                    />
                    <div className={styles.shineEffect} />
                    <div className={styles.glowEffect} />
                  </div>
                  
                  <AnimatePresence>
                    {loading && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        className={styles.processingLabel}
                      >
                        <Box sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          bgcolor: 'rgba(33,150,243,0.15)',
                          px: 2,
                          py: 1,
                          borderRadius: '8px',
                          backdropFilter: 'blur(4px)'
                        }}>
                          <div className={styles.processingDotAnimation} />
                          <Typography variant="subtitle2" className={styles.processingText}>
                            Processing your image...
                          </Typography>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className={styles.uploadCTA}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="contained"
                      className={styles.uploadButton}
                      onClick={() => fileInputRef.current.click()}
                      disabled={loading}
                      size="large"
                      sx={{
                        py: 2.5,
                        px: 6,
                        borderRadius: '10px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        fontSize: '1.1rem',
                        textTransform: 'none',
                      }}
                    >
                      <Typography variant="button" component="span" sx={{ 
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        color: '#fff'
                      }}>
                        {isDragActive ? 'Drop Image Here' : 'Upload Image'}
                      </Typography>
                    </Button>
                  </motion.div>
                  <Typography variant="caption" className={styles.uploadHint}>
                    Supported formats: JPEG, PNG, WEBP
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ImageUploadSection;